/**
 * MDTAid Clinical Decision Support Formulas
 * Converted from Excel formulas to TypeScript/JavaScript
 */

// PREDICT treatment benefit interface
export interface PredictTreatmentBenefit {
  treatment: string;
  additionalBenefit: string;      // e.g., "13.0% (7.7% - 18%)"
  additionalBenefitValue: number; // Numeric value for calculations
  additionalBenefitRange: {
    lower: number;
    upper: number;
  };
  overallSurvival: number;        // Overall survival percentage
}

// PREDICT data interface
export interface PredictData {
  yearsToConsider: number;        // 5, 10, or 15 years
  surgeryOnly: PredictTreatmentBenefit;
  hormoneTherapy?: PredictTreatmentBenefit;
  chemotherapy?: PredictTreatmentBenefit;
  trastuzumab?: PredictTreatmentBenefit;
  bisphosphonates?: PredictTreatmentBenefit;
  otherCausesMortality: number;   // e.g., 1% would die of other causes
  breastCancerExcludedSurvival: number; // e.g., 99% would survive if BC excluded
}

// Input data interface
export interface PatientData {
  // Demographics
  age: number;                    // G4
  menopausalStatus: string;       // G6
  
  // Tumor characteristics
  setting: string;                // G8 - Adjuvant/Non-Adjuvant
  surgery: string;                // G8 - Surgery type
  invasiveTumorSize: number;      // G10 - Size in mm
  grade: number;                  // G12 - Histological grade
  lymphNodes: number;             // G16 - Number of positive nodes
  erStatus: string;               // G18 - ER status
  her2Status: string;             // G20 - HER2 status
  npiScore: number;               // G29 - Nottingham Prognostic Index
  
  // Additional characteristics
  lvi: string;                    // I10 - Lymphovascular invasion
  inflammatory: string;           // J8 - Inflammatory breast cancer
  margins: string;                // I12 - Surgical margins
  marginsSize?: number;           // J12 - Margin size in mm
  prStatus: string;               // I18 - PR status
  wholeSize: number;              // G25 - Whole tumor size
  
  // PREDICT data (optional - can be calculated or provided)
  predictData?: PredictData;
}

// Output recommendations interface
export interface TreatmentRecommendations {
  staging: string;
  chemotherapy: string[];
  endocrineTherapy: string[];
  radiotherapy: string[];
  geneticTesting: string[];
  additionalTherapies: string[];
  monitoring: string[];
  predictData?: PredictData;
}

/**
 * MDT Clinical Decision Support Engine
 */
export class MDTAidFormulas {
  
  /**
   * STAGING REQUIREMENTS
   * Excel: N7 = IF(OR(G10>49, G16>3), "Staging required", "No Staging required")
   */
  static getStagingRequirement(data: PatientData): string {
    if (data.invasiveTumorSize > 49 || data.lymphNodes > 3) {
      return "Staging required";
    }
    return "No Staging required";
  }

  /**
   * NOTTINGHAM PROGNOSTIC INDEX (NPI)
   * Excel: G29 = (0.02*G10)+G12+Z72 where Z72 = IF(G16>3, 3, IF(G16=0,1,2))
   */
  static calculateNPI(invasiveTumorSize: number, grade: number, lymphNodes: number): number {
    const lymphNodeScore = lymphNodes === 0 ? 1 : lymphNodes <= 3 ? 2 : 3;
    return (0.02 * invasiveTumorSize) + grade + lymphNodeScore;
  }

  /**
   * CHEMOTHERAPY DECISION LOGIC
   * Multiple Excel formulas combined into comprehensive decision tree
   */
  static getChemotherapyRecommendations(data: PatientData): string[] {
    const recommendations: string[] = [];
    
    // Oncotype Request Logic
    // Excel: AD6 = IF(AND(G29>=3.4, G29<=5.4, G16<1,G18="Positive",G20="Negative"), "Request Oncotype", "")
    if (data.npiScore >= 3.4 && 
        data.npiScore <= 5.4 && 
        data.lymphNodes < 1 && 
        data.erStatus === "Positive" && 
        data.her2Status === "Negative") {
      recommendations.push("Request Oncotype");
    }
    
    // No Chemotherapy Indication
    // Excel: AD7 = IF(AND(G29<3.4, G16<1, G18="Positive", G20="Negative"), "No chemotherapy indicated", "")
    if (data.npiScore < 3.4 && 
        data.lymphNodes < 1 && 
        data.erStatus === "Positive" && 
        data.her2Status === "Negative") {
      recommendations.push("No chemotherapy indicated");
    }
    
    // High Risk Chemotherapy
    // Excel: AD8 = IF(AND(G29>5.4, G18="Positive", G20="Negative"), "Offer chemotherapy", "")
    if (data.npiScore > 5.4 && 
        data.erStatus === "Positive" && 
        data.her2Status === "Negative") {
      recommendations.push("Offer chemotherapy");
    }
    
    // Intermediate Risk Oncotype
    // Excel: AD9 = IF(AND(G29<=5.4,G16>0, G16<4,G18="Positive"),"Request Oncotype","")
    if (data.npiScore <= 5.4 && 
        data.lymphNodes > 0 && 
        data.lymphNodes < 4 && 
        data.erStatus === "Positive") {
      recommendations.push("Request Oncotype");
    }
    
    // Triple Negative Chemotherapy
    // Excel: AD10 = IF(AND(G18="Negative",G20="Negative", G10>4),"Offer chemotherapy","")
    if (data.erStatus === "Negative" && 
        data.her2Status === "Negative" && 
        data.invasiveTumorSize > 4) {
      recommendations.push("Offer chemotherapy");
    }
    
    // Small Triple Negative - Consult PREDICT
    // Excel: AD11 = IF(AND(G18="Negative",G20="Negative", G10<5),"Benefit of chemotherapy is low given size, consult PREDICT","")
    if (data.erStatus === "Negative" && 
        data.her2Status === "Negative" && 
        data.invasiveTumorSize < 5) {
      recommendations.push("Benefit of chemotherapy is low given size, consult PREDICT");
    }
    
    // HER2 Positive Chemotherapy
    // Excel: AD12 = IF(AND(G20="Positive", G10>4),"Offer chemotherapy and HER2 directed therapy","")
    if (data.her2Status === "Positive" && data.invasiveTumorSize > 4) {
      recommendations.push("Offer chemotherapy and HER2 directed therapy");
    }
    
    // Small HER2 Positive - Consult PREDICT
    // Excel: AD13 = IF(AND(G20="Positive", G10<5, G16=0),"Benefit of chemotherapy and HER2 directed therapy is low given size, consult PREDICT","")
    if (data.her2Status === "Positive" && 
        data.invasiveTumorSize < 5 && 
        data.lymphNodes === 0) {
      recommendations.push("Benefit of chemotherapy and HER2 directed therapy is low given size, consult PREDICT");
    }
    
    // Node Positive High Risk
    // Excel: AD14 = IF(AND(G16>3, G18="Positive", G20="Negative",AD8=""), "Offer chemotherapy", "")
    if (data.lymphNodes > 3 && 
        data.erStatus === "Positive" && 
        data.her2Status === "Negative" && 
        !recommendations.includes("Offer chemotherapy")) {
      recommendations.push("Offer chemotherapy");
    }
    
    return recommendations.filter(rec => rec !== "");
  }

  /**
   * ENDOCRINE THERAPY RECOMMENDATIONS
   * Multiple Excel formulas for hormone therapy selection
   */
  static getEndocrineTherapyRecommendations(data: PatientData): string[] {
    const recommendations: string[] = [];
    
    // Premenopausal Treatment
    // Excel: AD28 = IF(AND(G6="Premenopausal", G18="Positive"), "Offer Tamoxifen", "")
    if (data.menopausalStatus === "Premenopausal" && data.erStatus === "Positive") {
      recommendations.push("Offer Tamoxifen");
    }
    
    // Postmenopausal Treatment
    // Excel: AD29 = IF(AND(G6="Postmenopausal", G18="Positive"), "Offer Letrozole", "")
    if (data.menopausalStatus === "Postmenopausal" && data.erStatus === "Positive") {
      recommendations.push("Offer Letrozole");
    }
    
    const chemoRecs = this.getChemotherapyRecommendations(data);

    // Excel AD30: IF(AND(AD28="Offer Tamoxifen", G4<41, OR(N9="Offer chemotherapy", N9="Offer chemotherapy and HER2 directed therapy")), "Ovarian function suppression", "")
    if (
      recommendations.includes("Offer Tamoxifen") &&
      data.age < 41 &&
      (chemoRecs.includes("Offer chemotherapy") ||
        chemoRecs.includes("Offer chemotherapy and HER2 directed therapy"))
    ) {
      recommendations.push("Ovarian function suppression");
    }

    // Excel AD31: IF(AND(AD28="Offer Tamoxifen", G4>40, OR(N9="Offer chemotherapy", N9="Offer chemotherapy and HER2 directed therapy")), "Consider Ovarian function suppression", "")
    if (
      recommendations.includes("Offer Tamoxifen") &&
      data.age > 40 &&
      (chemoRecs.includes("Offer chemotherapy") ||
        chemoRecs.includes("Offer chemotherapy and HER2 directed therapy"))
    ) {
      recommendations.push("Consider Ovarian function suppression");
    }

    // Excel AD32: IF(AND(AD28="Offer Tamoxifen", G4<41, OR(N9="Benefit of chemotherapy is low given size, consult PREDICT", N9="Request Oncotype")), "Consider Ovarian function suppression", "")
    if (
      recommendations.includes("Offer Tamoxifen") &&
      data.age < 41 &&
      (chemoRecs.includes(
        "Benefit of chemotherapy is low given size, consult PREDICT",
      ) ||
        chemoRecs.includes("Request Oncotype"))
    ) {
      recommendations.push("Consider Ovarian function suppression");
    }
    
    return recommendations;
  }

  /**
   * TREATMENT DURATION LOGIC
   */
  static getTreatmentDuration(data: PatientData): string[] {
    const durations: string[] = [];
    const chemoRecs = this.getChemotherapyRecommendations(data);
    
    // Standard Duration for Node Positive
    // Excel: AC37 = IF(AND(G18="Positive", G16>0), "Duration: 10 years", "")
    if (data.erStatus === "Positive" && data.lymphNodes > 0) {
      durations.push("Duration: 10 years");
    }
    
    // Oncotype-Guided Duration
    // Excel: AC38 = IF(AND(N9="Request Oncotype", G16<1), "Duration to be determined by Oncotype", "")
    if (chemoRecs.includes("Request Oncotype") && data.lymphNodes < 1) {
      durations.push("Duration to be determined by Oncotype");
    }
    
    // Extended Duration with Chemotherapy
    // Excel: AC39 = IF(AND(G18="Positive", G16<1, N9="Offer chemotherapy"), "Duration: 10 years", "")
    if (data.erStatus === "Positive" && 
        data.lymphNodes < 1 && 
        chemoRecs.includes("Offer chemotherapy")) {
      durations.push("Duration: 10 years");
    }
    
    // Standard Duration for Low Risk
    // Excel: AC40 = IF(AND(G18="Positive", G16<1, N9="No chemotherapy indicated"), "Duration: 5 years", "")
    if (data.erStatus === "Positive" && 
        data.lymphNodes < 1 && 
        chemoRecs.includes("No chemotherapy indicated")) {
      durations.push("Duration: 5 years");
    }
    
    return durations;
  }

  /**
   * RADIOTHERAPY RECOMMENDATIONS
   */
  static getRadiotherapyRecommendations(data: PatientData): string[] {
    const recommendations: string[] = [];
    
    // Breast Conserving Surgery - Standard RT
    // Excel: AH48 = IF(G8="Breast-conserving", "Adjuvant radiotherapy to the breast", "")
    if (data.surgery === "Breast-conserving") {
      recommendations.push("Adjuvant radiotherapy to the breast");
    }
    
    // Mastectomy - Chest Wall RT
    // Excel: AH49 = IF(AND(G8="Mastectomy", G10>49), "Offer adjuvant radiotherapy to the Chest wall", "")
    if (data.surgery === "Mastectomy" && data.invasiveTumorSize > 49) {
      recommendations.push("Offer adjuvant radiotherapy to the Chest wall");
    }
    
    // RT Omission Eligibility
    // Excel: AH50 = IF(AND(G8="Breast-conserving", G18="Positive",G20="Negative",G12<3,G16<1,J12<20,G4>70),"Eligible for omission of radiotherapy","")
    if (data.surgery === "Breast-conserving" &&
        data.erStatus === "Positive" &&
        data.her2Status === "Negative" &&
        data.grade < 3 &&
        data.lymphNodes < 1 &&
        data.age > 70 &&
        (data.marginsSize === undefined || data.marginsSize < 20)) {
      recommendations.push("Eligible for omission of radiotherapy");
    }
    
    // Partial Breast RT
    // Excel: AH51 = IF(AND(G8="Breast-conserving", G18="Positive",G20="Negative",G12<3,G16<1,G10<30,G4>50),"(Consider partial breast radiotherapy)","")
    if (data.surgery === "Breast-conserving" && 
        data.erStatus === "Positive" && 
        data.her2Status === "Negative" && 
        data.grade < 3 && 
        data.lymphNodes < 1 && 
        data.invasiveTumorSize < 30 && 
        data.age > 50) {
      recommendations.push("(Consider partial breast radiotherapy)");
    }
    
    // Tumor Bed Boost - Young ER+
    // Excel: AH52 = IF(AND(G8="Breast-conserving", G4<50, G18="Positive"), "Consider tumour bed boost", "")
    if (data.surgery === "Breast-conserving" && 
        data.age < 50 && 
        data.erStatus === "Positive") {
      recommendations.push("Consider tumour bed boost");
    }
    
    // Tumor Bed Boost - Young ER-
    // Excel: AH53 = IF(AND(G8="Breast-conserving", G4<60, G18="Negative"), "offer tumour bed boost", "")
    if (data.surgery === "Breast-conserving" && 
        data.age < 60 && 
        data.erStatus === "Negative") {
      recommendations.push("offer tumour bed boost");
    }
    
    // Nodal Radiotherapy
    // Excel: AH55 = IF(G16>3, "Adjuvant radiotherapy to include the IMN and SCF chains", "")
    if (data.lymphNodes > 3) {
      recommendations.push("Adjuvant radiotherapy to include the IMN and SCF chains");
    }

    // Inflammatory BC → IMN/SCF RT
    // Excel: AH57 = IF(AND(AH55="", J8="Inflammatory"), "Adjuvant radiotherapy to include the IMN and SCF chains", "")
    if (
      !recommendations.includes("Adjuvant radiotherapy to include the IMN and SCF chains") &&
      data.inflammatory === "Yes"
    ) {
      recommendations.push("Adjuvant radiotherapy to include the IMN and SCF chains");
    }
    
    return recommendations;
  }

  /**
   * GENETIC TESTING RECOMMENDATIONS
   */
  static getGeneticTestingRecommendations(data: PatientData): string[] {
    const recommendations: string[] = [];
    
    // Young Patient Testing
    // Excel: AD76 = IF(AND(G4<40,G4>0), "Offer R208 Genetic panel", "")
    if (data.age < 40 && data.age > 0) {
      recommendations.push("Offer R208 Genetic panel");
    }
    
    // Middle-Age Triple Negative Testing
    // Excel: AD77 = IF(AND(G18="Negative", G20="Negative", G4<60, G4>49), "Offer R208 Genetic panel", "")
    if (data.erStatus === "Negative" && 
        data.her2Status === "Negative" && 
        data.age < 60 && 
        data.age > 49) {
      recommendations.push("Offer R208 Genetic panel");
    }
    
    // BRCA Testing for Olaparib Eligibility
    // Excel: AD78 = IF(AND(G16>3, G20="Negative",AD76=""), "Offer R.444 panel as eligible for Olaparib if BRCA positive","")
    if (data.lymphNodes > 3 && 
        data.her2Status === "Negative" && 
        !recommendations.includes("Offer R208 Genetic panel")) {
      recommendations.push("Offer R.444 panel as eligible for Olaparib if BRCA positive");
    }

    // Excel AD79: IF(AND(G16>3, G20="Negative", AD76="Offer R208 Genetic panel"), "eligible for Olaparib if BRCA positive", "")
    if (
      data.lymphNodes > 3 &&
      data.her2Status === "Negative" &&
      recommendations.includes("Offer R208 Genetic panel")
    ) {
      recommendations.push("eligible for Olaparib if BRCA positive");
    }
    
    return recommendations;
  }

  /**
   * ADDITIONAL THERAPIES
   */
  static getAdditionalTherapies(data: PatientData): string[] {
    const recommendations: string[] = [];
    const chemoRecs = this.getChemotherapyRecommendations(data);
    
    // Bisphosphonates
    // Excel: AB83 = IF(AND(G6="Postmenopausal", N9="Offer Chemotherapy"), "Offer Bisphosphonates", "")
    if (data.menopausalStatus === "Postmenopausal" && 
        chemoRecs.includes("Offer chemotherapy")) {
      recommendations.push("Offer Bisphosphonates");
    }

    // Excel AB84: IF(AND(G6="Postmenopausal", N9="Request Oncotype"), "Bisphosphonates guided by Oncotype", "")
    if (
      data.menopausalStatus === "Postmenopausal" &&
      chemoRecs.includes("Request Oncotype")
    ) {
      recommendations.push("Bisphosphonates guided by Oncotype");
    }
    
    // CDK4/6 Inhibitors - High Risk
    // Excel: AC107 = IF(AND(G18="Positive", G20="Negative", G12>2, G16>=1), "Eligible for Abemacilib", "")
    if (data.erStatus === "Positive" && 
        data.her2Status === "Negative" && 
        data.grade > 2 && 
        data.lymphNodes >= 1) {
      recommendations.push("Eligible for Abemacilib");
    }
    
    // CDK4/6 Inhibitors - Node Positive
    // Excel: AC108 = IF(AND(G18="Positive", G20="Negative", G16>=4,AC107=""), "Eligible for Abemacilib", "")
    if (data.erStatus === "Positive" && 
        data.her2Status === "Negative" && 
        data.lymphNodes >= 4 && 
        !recommendations.includes("Eligible for Abemacilib")) {
      recommendations.push("Eligible for Abemacilib");
    }
    
    return recommendations;
  }

  /**
   * MONITORING REQUIREMENTS
   */
  static getMonitoringRequirements(data: PatientData): string[] {
    const recommendations: string[] = [];
    const chemoRecs = this.getChemotherapyRecommendations(data);
    const endocrineRecs = this.getEndocrineTherapyRecommendations(data);
    
    // Cardiac Monitoring for Chemotherapy
    // Excel: N10 = IF(OR(N9="Offer chemotherapy"), "Please request ECHO","")
    if (chemoRecs.some(rec => rec === "Offer chemotherapy" || rec === "Offer chemotherapy and HER2 directed therapy")) {
      recommendations.push("Please request ECHO");
    }
    
    // Bone Health Monitoring for AI
    // Excel: Z44 = IF(AND(N12="Offer Letrozole", N16=""), "Please request baseline DEXA scan", "")
    const additionalRecs = this.getAdditionalTherapies(data);
    const bisphosOffered = additionalRecs.some(
      (r) => r.includes("Bisphosphonates") || r.includes("Oncotype"),
    );
    if (endocrineRecs.includes("Offer Letrozole") && !bisphosOffered) {
      recommendations.push("Please request baseline DEXA scan");
    }
    
    return recommendations;
  }

  /**
   * PREDICT DATA INTEGRATION
   * Methods for working with PREDICT survival and benefit data
   */
  
  /**
   * Create PREDICT treatment benefit object
   */
  static createPredictBenefit(
    treatment: string,
    additionalBenefit: string,
    additionalBenefitValue: number,
    lowerRange: number,
    upperRange: number,
    overallSurvival: number
  ): PredictTreatmentBenefit {
    return {
      treatment,
      additionalBenefit,
      additionalBenefitValue,
      additionalBenefitRange: {
        lower: lowerRange,
        upper: upperRange
      },
      overallSurvival
    };
  }

  /**
   * Generate sample PREDICT data based on patient characteristics
   * This would typically come from the PREDICT API or database
   */
  static generateSamplePredictData(data: PatientData, years: number = 10): PredictData {
    // Base survival for surgery only (simplified calculation)
    const baseAge = data.age;
    const baseSurvival = Math.max(20, 90 - (baseAge - 50) * 0.5 - data.lymphNodes * 5 - (data.grade - 1) * 3);
    
    const predictData: PredictData = {
      yearsToConsider: years,
      surgeryOnly: this.createPredictBenefit(
        "Surgery only",
        "-",
        0,
        0,
        0,
        Math.round(baseSurvival)
      ),
      otherCausesMortality: Math.min(10, Math.max(1, Math.round((baseAge - 40) * 0.2))),
      breastCancerExcludedSurvival: 99
    };

    // Track cumulative survival
    let cumulativeSurvival = Math.round(baseSurvival);

    // Add hormone therapy benefit if ER+
    if (data.erStatus === "Positive") {
      const hormoneBenefit = data.menopausalStatus === "Postmenopausal" ? 13.0 : 11.0;
      const lowerRange = hormoneBenefit - 5.3;
      const upperRange = hormoneBenefit + 5.0;
      cumulativeSurvival += Math.round(hormoneBenefit);
      
      predictData.hormoneTherapy = this.createPredictBenefit(
        "+ Hormone therapy",
        `${hormoneBenefit.toFixed(1)}% (${lowerRange.toFixed(1)}% - ${upperRange.toFixed(1)}%)`,
        hormoneBenefit,
        lowerRange,
        upperRange,
        cumulativeSurvival
      );
    }

    // Add chemotherapy benefit
    const chemoBenefit = data.erStatus === "Negative" ? 16.0 : 14.1;
    const chemoLower = chemoBenefit - 4.1;
    const chemoUpper = chemoBenefit + 3.9;
    cumulativeSurvival += Math.round(chemoBenefit);
    
    predictData.chemotherapy = this.createPredictBenefit(
      "+ Chemotherapy",
      `${chemoBenefit.toFixed(1)}% (${chemoLower.toFixed(1)}% - ${chemoUpper.toFixed(1)}%)`,
      chemoBenefit,
      chemoLower,
      chemoUpper,
      cumulativeSurvival
    );

    // Add trastuzumab benefit if HER2+
    if (data.her2Status === "Positive") {
      const trastuzumabBenefit = 9.1;
      const trastuzumabLower = 6.3;
      const trastuzumabUpper = 13.0;
      cumulativeSurvival += Math.round(trastuzumabBenefit);
      
      predictData.trastuzumab = this.createPredictBenefit(
        "+ Trastuzumab",
        `${trastuzumabBenefit.toFixed(1)}% (${trastuzumabLower.toFixed(1)}% - ${trastuzumabUpper.toFixed(1)}%)`,
        trastuzumabBenefit,
        trastuzumabLower,
        trastuzumabUpper,
        cumulativeSurvival
      );
    }

    // Add bisphosphonates benefit if postmenopausal
    if (data.menopausalStatus === "Postmenopausal") {
      const bisphosBenefit = 4.2;
      const bisphosLower = 1.6;
      const bisphosUpper = 6.5;
      cumulativeSurvival += Math.round(bisphosBenefit);
      
      predictData.bisphosphonates = this.createPredictBenefit(
        "+ Bisphosphonates",
        `${bisphosBenefit.toFixed(1)}% (${bisphosLower.toFixed(1)}% - ${bisphosUpper.toFixed(1)}%)`,
        bisphosBenefit,
        bisphosLower,
        bisphosUpper,
        cumulativeSurvival
      );
    }

    return predictData;
  }

  /**
   * Get PREDICT-based treatment recommendations
   * Integrates PREDICT data with MDT decision logic
   */
  static getPredictBasedRecommendations(data: PatientData): string[] {
    const recommendations: string[] = [];
    const predictData = data.predictData || this.generateSamplePredictData(data);
    
    // Use PREDICT data to refine chemotherapy recommendations
    if (predictData.chemotherapy && predictData.chemotherapy.additionalBenefitValue < 5) {
      recommendations.push("Benefit of chemotherapy is low according to PREDICT, consult PREDICT tool");
    } else if (predictData.chemotherapy && predictData.chemotherapy.additionalBenefitValue > 10) {
      recommendations.push("Significant chemotherapy benefit predicted by PREDICT");
    }
    
    // Hormone therapy recommendations based on PREDICT
    if (predictData.hormoneTherapy && predictData.hormoneTherapy.additionalBenefitValue > 8) {
      recommendations.push("Substantial hormone therapy benefit predicted by PREDICT");
    }
    
    // Trastuzumab recommendations
    if (predictData.trastuzumab && predictData.trastuzumab.additionalBenefitValue > 7) {
      recommendations.push("Significant trastuzumab benefit predicted by PREDICT");
    }
    
    return recommendations;
  }

  /**
   * Format PREDICT data for display
   */
  static formatPredictData(predictData: PredictData): string {
    const treatments: string[] = [];
    
    treatments.push(`Surgery only: ${predictData.surgeryOnly.overallSurvival}%`);
    
    if (predictData.hormoneTherapy) {
      treatments.push(`+ Hormone therapy: ${predictData.hormoneTherapy.additionalBenefit} → ${predictData.hormoneTherapy.overallSurvival}%`);
    }
    
    if (predictData.chemotherapy) {
      treatments.push(`+ Chemotherapy: ${predictData.chemotherapy.additionalBenefit} → ${predictData.chemotherapy.overallSurvival}%`);
    }
    
    if (predictData.trastuzumab) {
      treatments.push(`+ Trastuzumab: ${predictData.trastuzumab.additionalBenefit} → ${predictData.trastuzumab.overallSurvival}%`);
    }
    
    if (predictData.bisphosphonates) {
      treatments.push(`+ Bisphosphonates: ${predictData.bisphosphonates.additionalBenefit} → ${predictData.bisphosphonates.overallSurvival}%`);
    }
    
    return `PREDICT ${predictData.yearsToConsider}-year survival:\n${treatments.join('\n')}`;
  }

  /**
   * COMPREHENSIVE TREATMENT RECOMMENDATIONS
   * Main function that combines all recommendation types including PREDICT data
   */
  static getComprehensiveRecommendations(data: PatientData): TreatmentRecommendations {
    // Generate or use existing PREDICT data
    const predictData = data.predictData || this.generateSamplePredictData(data);
    
    return {
      staging: this.getStagingRequirement(data),
      chemotherapy: [
        ...this.getChemotherapyRecommendations(data),
        ...this.getPredictBasedRecommendations(data)
      ],
      endocrineTherapy: this.getEndocrineTherapyRecommendations(data),
      radiotherapy: this.getRadiotherapyRecommendations(data),
      geneticTesting: this.getGeneticTestingRecommendations(data),
      additionalTherapies: this.getAdditionalTherapies(data),
      monitoring: this.getMonitoringRequirements(data),
      predictData: predictData
    };
  }
}

/**
 * UTILITY FUNCTIONS
 */

// Text joining function (replaces Excel TEXTJOIN)
export function textJoin(separator: string, ignoreEmpty: boolean, ...values: (string | string[])[]): string {
  const flatValues = values.flat().filter(val => ignoreEmpty ? val !== "" : true);
  return flatValues.join(separator);
}

// Logical AND function
export function and(...conditions: boolean[]): boolean {
  return conditions.every(condition => condition === true);
}

// Logical OR function
export function or(...conditions: boolean[]): boolean {
  return conditions.some(condition => condition === true);
}

// Example usage and test data
export const samplePatientData: PatientData = {
  age: 45,
  menopausalStatus: "Premenopausal",
  setting: "Adjuvant",
  surgery: "Breast-conserving",
  invasiveTumorSize: 25,
  grade: 2,
  lymphNodes: 1,
  erStatus: "Positive",
  her2Status: "Negative",
  npiScore: 4.2,
  lvi: "Present",
  inflammatory: "No",
  margins: "Clear",
  prStatus: "Positive",
  wholeSize: 25
};

// Example PREDICT data matching the format from the image
export const samplePredictData: PredictData = {
  yearsToConsider: 10,
  surgeryOnly: {
    treatment: "Surgery only",
    additionalBenefit: "-",
    additionalBenefitValue: 0,
    additionalBenefitRange: { lower: 0, upper: 0 },
    overallSurvival: 35
  },
  hormoneTherapy: {
    treatment: "+ Hormone therapy",
    additionalBenefit: "13.0% (7.7% - 18%)",
    additionalBenefitValue: 13.0,
    additionalBenefitRange: { lower: 7.7, upper: 18.0 },
    overallSurvival: 49
  },
  chemotherapy: {
    treatment: "+ Chemotherapy",
    additionalBenefit: "14.1% (10% - 18%)",
    additionalBenefitValue: 14.1,
    additionalBenefitRange: { lower: 10.0, upper: 18.0 },
    overallSurvival: 63
  },
  trastuzumab: {
    treatment: "+ Trastuzumab",
    additionalBenefit: "9.1% (6.3% - 13%)",
    additionalBenefitValue: 9.1,
    additionalBenefitRange: { lower: 6.3, upper: 13.0 },
    overallSurvival: 72
  },
  bisphosphonates: {
    treatment: "+ Bisphosphonates",
    additionalBenefit: "4.2% (1.6% - 6.5%)",
    additionalBenefitValue: 4.2,
    additionalBenefitRange: { lower: 1.6, upper: 6.5 },
    overallSurvival: 76
  },
  otherCausesMortality: 1,
  breastCancerExcludedSurvival: 99
};
